import React from "react";
import KpiTile from "./KpiTile";
import { DashboardKpis } from "../../../../types/onboardingAnalytics";
import {
  formatCount,
  formatDays,
  formatPercent,
  hasMeasurement,
} from "../../../../utils/analyticsFormat";

interface KpiTilesProps {
  kpis: DashboardKpis;
}

const NO_DATA = "No data yet";

/**
 * The nine headline figures.
 *
 * Two absences are deliberate and should not be "fixed" without reading
 * research R4 first:
 *
 * - **No period selector.** The API accepts a `period` parameter, validates it
 *   and then ignores it — the response always carries both the weekly and the
 *   monthly counts. A selector would be a control that changes nothing (FR-004).
 * - **No deltas or trend arrows.** No prior-period value is returned, so any
 *   change indicator would have to be invented (FR-005).
 *
 * The conversion rate and the average duration both arrive as `0.0` for an
 * empty database *and* for a genuine zero. The pipeline totals disambiguate:
 * with nothing live there is nothing to average, so the tile says so rather than
 * stating a measurement that was never taken (R10).
 */
const KpiTiles: React.FC<KpiTilesProps> = ({ kpis }) => {
  const anythingLive = hasMeasurement(kpis.totalCustomersLive);
  const anyProspects =
    hasMeasurement(kpis.totalInPipeline) || anythingLive;

  const conversion = anyProspects
    ? formatPercent(kpis.overallConversionRatePct)
    : null;
  const duration = anythingLive
    ? formatDays(kpis.avgOnboardingDurationDays, kpis.totalCustomersLive)
    : null;

  return (
    <div className="grid">
      <div className="col-12 md:col-6 lg:col-3">
        <KpiTile
          title="In pipeline"
          icon="pi-sync"
          value={formatCount(kpis.totalInPipeline)}
          hint="Not yet live, not resigned"
        />
      </div>
      <div className="col-12 md:col-6 lg:col-3">
        <KpiTile
          title="Live customers"
          icon="pi-check-circle"
          value={formatCount(kpis.totalCustomersLive)}
          hint="All time"
        />
      </div>
      <div className="col-12 md:col-6 lg:col-3">
        <KpiTile
          title="New prospects"
          icon="pi-user-plus"
          value={formatCount(kpis.newProspectsThisMonth)}
          suffix="this month"
          hint={`${formatCount(kpis.newProspectsThisWeek)} in the last 7 days`}
        />
      </div>
      <div className="col-12 md:col-6 lg:col-3">
        <KpiTile
          title="New customers"
          icon="pi-star"
          value={formatCount(kpis.newCustomersThisMonth)}
          suffix="this month"
          hint={`${formatCount(kpis.newCustomersThisWeek)} in the last 7 days`}
        />
      </div>
      <div className="col-12 md:col-6 lg:col-4">
        <KpiTile
          title="Conversion to live"
          icon="pi-percentage"
          value={conversion ?? NO_DATA}
          muted={!conversion}
          hint="Form submitted → station public"
        />
      </div>
      <div className="col-12 md:col-6 lg:col-4">
        <KpiTile
          title="Average onboarding"
          icon="pi-clock"
          value={duration ?? NO_DATA}
          muted={!duration}
          hint="First submission → station public"
        />
      </div>
      <div className="col-12 md:col-12 lg:col-4">
        <KpiTile
          title="Overdue"
          icon="pi-exclamation-triangle"
          value={formatCount(kpis.overdueCount)}
          hint="Past the threshold for their stage"
        />
      </div>
    </div>
  );
};

export default KpiTiles;
